import React, { useState, useEffect } from 'react';
import { useTranslation } from '../context/TranslationContext';
import { useAuth } from '../context/AuthContext';
import { API_ENDPOINTS } from '../utils/config';

interface RequestEmailsTabProps {
  request: any;
  requestId: string;
}

const RequestEmailsTab: React.FC<RequestEmailsTabProps> = ({
  request,
  requestId
}) => {
  const { t } = useTranslation();
  const { token } = useAuth();

  // Email states - now managed internally
  const [emails, setEmails] = useState<any>({});
  const [selectedEmail, setSelectedEmail] = useState<any>(null);
  const [emailHistory, setEmailHistory] = useState<any[]>([]);
  const [isLoadingEmails, setIsLoadingEmails] = useState(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [replyMessage, setReplyMessage] = useState('');
  const [isSendingReply, setIsSendingReply] = useState(false);
  const [emailFilters, setEmailFilters] = useState({
    status: 'all',
    supplier: 'all'
  });
  const [selectedCCRecipients, setSelectedCCRecipients] = useState<string[]>([]);
  const [availableReplyEmails, setAvailableReplyEmails] = useState<string[]>([]);
  const [availableCCEmails, setAvailableCCEmails] = useState<string[]>([]);
  const [replyToEmail, setReplyToEmail] = useState<any>(null);
  const [replySubject, setReplySubject] = useState('');
  const [replyContent, setReplyContent] = useState('');
  const [replyAttachments, setReplyAttachments] = useState<File[]>([]);
  const [selectedRecipients, setSelectedRecipients] = useState<string[]>([]);
  const [replyStatusMessage, setReplyStatusMessage] = useState('');
  const [showToInput, setShowToInput] = useState(false);
  const [showCCInput, setShowCCInput] = useState(false);
  const [tempToEmail, setTempToEmail] = useState('');
  const [tempCCEmail, setTempCCEmail] = useState('');
  const [selectedReplyEmail, setSelectedReplyEmail] = useState<string | null>(null);

  // Load emails when component mounts
  useEffect(() => {
    if (requestId && token) {
      fetchEmails();
    }
  }, [requestId, token]);

  // Helper function to organize emails by supplier
  const organizeEmailsBySupplier = (allEmails: any[]) => {
    if (!allEmails || allEmails.length === 0) return [];
    
    console.log('🔍 DEBUG: Organizing emails by supplier:', allEmails.length, 'emails');
    console.log('🔍 DEBUG: Supplier names found:', allEmails.map(e => e.supplier_name));
    
    // Group by supplier_name (same supplier = same chat)
    const supplierGroups = new Map();
    
    allEmails.forEach(email => {
      const supplierName = email.supplier_name;
      
      if (supplierName === undefined || supplierName === null) {
        return; // Skip emails without supplier_name
      }
      
      // Normalize supplier name for consistent grouping (trim whitespace, lowercase for comparison)
      const normalizedSupplierName = supplierName.trim();
      
      if (!supplierGroups.has(normalizedSupplierName)) {
        supplierGroups.set(normalizedSupplierName, []);
      }
      
      supplierGroups.get(normalizedSupplierName).push(email);
    });
    
    // Return groups as separate arrays (for left sidebar)
    const result = Array.from(supplierGroups.entries()).map(([supplierName, emails]) => ({
      supplier_name: supplierName,
      emails: emails.sort((a: any, b: any) => {
        const timeA = a.sent_at || a.created_at || a.timestamp;
        const timeB = b.sent_at || b.created_at || b.timestamp;
        return timeA.localeCompare(timeB);
      })
    }));
    
    console.log('🔍 DEBUG: Final supplier groups:', result.map(g => ({ name: g.supplier_name, count: g.emails.length })));
    
    return result;
  };

  // Email timestamp formatting function - uses UTC times from database
  const formatTime = (timestamp: string | Date) => {
    if (!timestamp) return '';
    
    const date = new Date(timestamp);
    const now = new Date();
    
    // Date objects store time in UTC milliseconds, so direct comparison is correct
    // For display purposes, we want to show the UTC time consistently
    const diffInMilliseconds = now.getTime() - date.getTime();
    const diffInMinutes = Math.floor(diffInMilliseconds / (1000 * 60));
    const diffInHours = Math.floor(diffInMinutes / 60);
    const diffInDays = Math.floor(diffInHours / 24);
    
    if (diffInHours < 1) {
      return `${diffInMinutes}m`;
    } else if (diffInHours < 24) {
      return `${diffInHours}h`;
    } else {
      return `${diffInDays}d`;
    }
  };

  // Handle reply to specific email
  const handleReplyToEmail = (email: any) => {
    console.log('📧 Reply to email:', email);
    
    // Set the email to reply to
    setReplyToEmail(email);
    
    // Set default subject
    const replySubject = email.subject?.startsWith('Re:') ? email.subject : `Re: ${email.subject || 'No Subject'}`;
    setReplySubject(replySubject);
    
    // Set default content
    setReplyContent('');
    
    // Set default recipients based on email direction
    let defaultToEmails: string[] = [];
    let defaultCCEmails: string[] = [];
    
    if (email.direction === 'sent') {
      // If replying to a sent email, reply to the original recipients
      if (email.to_email) {
        defaultToEmails.push(email.to_email);
      }
      if (email.additional_to_recipients && Array.isArray(email.additional_to_recipients)) {
        defaultToEmails.push(...email.additional_to_recipients);
      }
      if (email.cc_recipients && Array.isArray(email.cc_recipients)) {
        defaultCCEmails.push(...email.cc_recipients);
      }
    } else {
      // If replying to a received email, reply to the sender
      if (email.from_email) {
        // Extract email from "Name <email>" format
        const emailMatch = email.from_email.match(/<([^>]+)>/);
        const extractedEmail = emailMatch ? emailMatch[1] : email.from_email;
        defaultToEmails.push(extractedEmail);
        console.log('📧 DEBUG: Extracted email from sender:', extractedEmail);
      }
      
      // Also include CC recipients from the received email
      if (email.cc_recipients && Array.isArray(email.cc_recipients)) {
        defaultCCEmails.push(...email.cc_recipients);
        console.log('📧 DEBUG: Added CC recipients from received email:', email.cc_recipients);
      }
    }
    
    setSelectedRecipients(defaultToEmails);
    setSelectedCCRecipients(defaultCCEmails);
    
    // Also set available emails for the reply form
    setAvailableReplyEmails(defaultToEmails);
    setAvailableCCEmails(defaultCCEmails);
    
    // If we have CC recipients, also add them to available reply emails
    if (defaultCCEmails.length > 0) {
      const combinedEmails = [...defaultToEmails, ...defaultCCEmails];
      const uniqueEmails = Array.from(new Set(combinedEmails));
      setAvailableReplyEmails(uniqueEmails);
      console.log('📧 DEBUG: Combined available emails (TO + CC):', uniqueEmails);
    }
    
    console.log('📧 DEBUG: Set default TO recipients:', defaultToEmails);
    console.log('📧 DEBUG: Set default CC recipients:', defaultCCEmails);
    console.log('📧 DEBUG: Set available reply emails:', defaultToEmails);
    console.log('📧 DEBUG: Set available CC emails:', defaultCCEmails);
    console.log('📧 DEBUG: Reply form should be visible now');
    
    // Update status message
    setReplyStatusMessage('✅ Reply form ready - Select recipients and type your message');
    
    // Reply form is already visible inline
  };

  // Fetch emails function
  const fetchEmails = async () => {
    if (!token || !requestId) return;
    
    try {
      setIsLoadingEmails(true);
      const response = await fetch(API_ENDPOINTS.SENT_EMAILS, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const result = await response.json();
        console.log('🔍 Sent Emails Response:', result);
        
        if (result.success) {
          // Ensure result.data is an array
          const emailsData = Array.isArray(result.data) ? result.data : [];
          
          // Filter emails for this specific request
          const requestEmails = emailsData.filter((email: any) => 
            email.request_id === requestId
          );
          
          // Organize emails by supplier for left sidebar
          const organizedEmails = organizeEmailsBySupplier(requestEmails);
          setEmails(organizedEmails);
        } else {
          console.error('❌ Sent emails API error:', result.message);
          setEmails({});
        }
      } else {
        console.error('❌ Sent emails HTTP error:', response.status);
        setEmails({});
      }
    } catch (error) {
      console.error('Error fetching emails:', error);
    } finally {
      setIsLoadingEmails(false);
    }
  };

  // Fetch email history function
  const fetchEmailHistory = async (supplierName: string) => {
    if (!token || !requestId) return;
    
    try {
      setIsLoadingHistory(true);
      const response = await fetch(API_ENDPOINTS.EMAIL_HISTORY(requestId, supplierName), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('🔍 Email History Response:', data);
        
        if (data.success) {
          // Use data.email_history instead of data.data
          const emailHistoryData = Array.isArray(data.data.email_history) ? data.data.email_history : [];
          const supplierData = data.data.supplier || {};
          console.log('🔍 Email History Data:', emailHistoryData);
          console.log('🔍 Supplier Data:', supplierData);
          setEmailHistory(emailHistoryData);
          
          // Set available reply emails - TO ve CC'yi ayır
          const allToEmails: string[] = [];
          const allCCEmails: string[] = [];
          
          // Helper function to extract clean email from formatted string
          const extractCleanEmail = (emailStr: string): string => {
            if (!emailStr) return '';
            // Extract email from "Name <email@domain.com>" format
            const emailMatch = emailStr.match(/<([^>]+)>/) || emailStr.match(/([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/);
            return emailMatch ? emailMatch[1] : emailStr.trim();
          };
          
          // Received email'lerden from_email'leri al (TO olarak)
          emailHistoryData
            .filter((email: any) => email.direction === 'received')
            .forEach((email: any) => {
              if (email.from_email) {
                const cleanEmail = extractCleanEmail(email.from_email);
                if (cleanEmail) allToEmails.push(cleanEmail);
              }
              // CC recipient'ları CC olarak ekle
              if (email.cc_recipients && Array.isArray(email.cc_recipients)) {
                email.cc_recipients.forEach((ccEmail: string) => {
                  const cleanCCEmail = extractCleanEmail(ccEmail);
                  if (cleanCCEmail) allCCEmails.push(cleanCCEmail);
                });
              }
            });
          
          // Sent email'lerden to_email'leri al (TO olarak)
          emailHistoryData
            .filter((email: any) => email.direction === 'sent')
            .forEach((email: any) => {
              if (email.to_email) {
                const cleanEmail = extractCleanEmail(email.to_email);
                if (cleanEmail) allToEmails.push(cleanEmail);
              }
              // Sent email'lerdeki CC recipient'ları CC olarak ekle
              if (email.cc_recipients && Array.isArray(email.cc_recipients)) {
                email.cc_recipients.forEach((ccEmail: string) => {
                  const cleanCCEmail = extractCleanEmail(ccEmail);
                  if (cleanCCEmail) allCCEmails.push(cleanCCEmail);
                });
              }
            });
          
          // Supplier'ın additional_emails'ini de dahil et
          if (supplierData.additional_emails && Array.isArray(supplierData.additional_emails)) {
            supplierData.additional_emails.forEach((email: string) => {
              const cleanEmail = extractCleanEmail(email);
              if (cleanEmail) {
                allToEmails.push(cleanEmail);
                allCCEmails.push(cleanEmail); // Additional emails hem TO hem CC olarak kullanılabilir
              }
            });
            console.log('📧 DEBUG: Added supplier additional_emails:', supplierData.additional_emails);
          }
          
          // Duplicate'ları kaldır ve unique yap
          const toEmails = Array.from(new Set(allToEmails)).filter(email => email && email.trim() !== '');
          const ccEmails = Array.from(new Set(allCCEmails)).filter(email => email && email.trim() !== '');
          
          console.log('🔍 DEBUG: TO emails collected:', allToEmails);
          console.log('🔍 DEBUG: CC emails collected:', allCCEmails);
          console.log('🔍 DEBUG: Unique TO emails:', toEmails);
          console.log('🔍 DEBUG: Unique CC emails:', ccEmails);
          
          setAvailableReplyEmails(toEmails);
          setAvailableCCEmails(ccEmails);
          setSelectedRecipients(toEmails);
          setSelectedCCRecipients(ccEmails);
          console.log('🔍 DEBUG: Set TO recipients to:', toEmails);
          console.log('🔍 DEBUG: Set CC recipients to:', ccEmails);
        } else {
          console.error('❌ Email history API error:', data.message);
          setEmailHistory([]);
        }
      } else {
        console.error('❌ Email history HTTP error:', response.status);
        setEmailHistory([]);
      }
    } catch (error) {
      console.error('Error fetching email history:', error);
    } finally {
      setIsLoadingHistory(false);
    }
  };

  // Email click handler
  const handleEmailClick = (email: any) => {
    console.log('🔍 DEBUG: Email clicked:', email);
    console.log('🔍 DEBUG: Setting selectedEmail to:', email);
    setSelectedEmail(email);
    fetchEmailHistory(email.supplier_name);
  };

  // Send reply handler
  const handleSendReply = async () => {
    console.log('🔍 DEBUG: handleSendReply called!');
    console.log('🔍 DEBUG: token:', token ? 'Present' : 'Missing');
    console.log('🔍 DEBUG: selectedEmail:', selectedEmail);
    console.log('🔍 DEBUG: replyToEmail:', replyToEmail);
    console.log('🔍 DEBUG: replyContent:', replyContent);
    console.log('🔍 DEBUG: selectedRecipients:', selectedRecipients);
    
    if (!token || !selectedEmail || !replyContent.trim() || selectedRecipients.length === 0) {
      console.log('❌ DEBUG: Early return - missing required data');
      return;
    }
    
    console.log('🔍 DEBUG: Sending reply with token:', token ? 'Token present' : 'No token');
    console.log('🔍 DEBUG: API endpoint:', API_ENDPOINTS.SEND_REPLY);
    
    // Use replyToEmail's subject if available, otherwise use selectedEmail's subject
    const baseSubject = replyToEmail?.subject || selectedEmail.subject || 'Procurement Request';
    const replySubject = baseSubject.startsWith('Re:') ? baseSubject : `Re: ${baseSubject}`;
    
    
    try {
      setIsSendingReply(true);
      
      // Create FormData for file uploads
      const formData = new FormData();
      formData.append('request_id', requestId);
      formData.append('supplier_name', selectedEmail.supplier_name);
      formData.append('subject', replySubject);
      formData.append('content', replyContent);
      
      // Add recipients
      selectedRecipients.forEach(email => {
        formData.append('recipient_emails', email);
      });
      
      // Add CC recipients
      selectedCCRecipients.forEach(email => {
        formData.append('cc_recipients', email);
      });
      
      if (replyToEmail?._id) {
        formData.append('reply_to_email_id', replyToEmail._id);
      }
      
      if (replyToEmail?.message_id) {
        formData.append('reply_to_message_id', replyToEmail.message_id);
      }
      
      // Add attachments
      replyAttachments.forEach((file, index) => {
        formData.append(`attachment_${index}`, file);
      });

      const response = await fetch(API_ENDPOINTS.SEND_REPLY, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
          // Don't set Content-Type header, let browser set it with boundary for FormData
        },
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          // Show success popup with reply details
          const ccText = selectedCCRecipients.length > 0 ? `\nCC: ${selectedCCRecipients.join(', ')}` : '';
          const replyDetails = `Reply sent successfully!\n\nTo: ${selectedRecipients.join(', ')}${ccText}\nSubject: ${replySubject}\nSupplier: ${selectedEmail.supplier_name}\n\nThe conversation has been updated.`;
          window.alert(replyDetails);
          setReplyContent('');
          setReplyAttachments([]);
          setReplyStatusMessage('');
          setShowToInput(false);
          setShowCCInput(false);
          setTempToEmail('');
          setTempCCEmail('');
          setSelectedReplyEmail(null);
          // Refresh email history
          fetchEmailHistory(selectedEmail.supplier_name);
        }
      }
    } catch (error) {
      console.error('Error sending reply:', error);
    } finally {
      setIsSendingReply(false);
    }
  };

  // Reply recipient handlers
  const handleReplyRecipientToggle = (email: string) => {
    setSelectedRecipients(prev => {
      if (prev.includes(email)) {
        return prev.filter(e => e !== email);
      } else {
        return [...prev, email];
      }
    });
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files) {
      const fileArray = Array.from(files);
      setReplyAttachments(fileArray);
    }
  };

  const removeAttachment = (index: number) => {
    const newAttachments = replyAttachments.filter((_, i) => i !== index);
    setReplyAttachments(newAttachments);
  };

  const handleSelectAllReplyRecipients = () => {
    setSelectedRecipients([...availableReplyEmails]);
  };

  const handleDeselectAllReplyRecipients = () => {
    setSelectedRecipients([]);
  };

  // Helper functions
  const getSupplierNameFromIndex = (supplierName: string) => {
    if (!supplierName || supplierName === 'Unknown Supplier') return 'Unknown Supplier';
    return supplierName;
  };

  const getSupplierEmailFromIndex = (supplierName: string) => {
    if (!request || !supplierName) return '';
    
    // Find supplier email from request data
    const supplier = request.suppliers?.find((s: any) => s.supplier_name === supplierName);
    return supplier?.supplier_email || '';
  };

  const getSupplierPhoneFromIndex = (supplierName: string) => {
    if (!request || !supplierName) return '';
    
    const supplier = request.suppliers?.find((s: any) => s.supplier_name === supplierName);
    return supplier?.supplier_phone || '';
  };

  const getSupplierRegionFromIndex = (supplierName: string) => {
    if (!request || !supplierName) return '';
    
    const supplier = request.suppliers?.find((s: any) => s.supplier_name === supplierName);
    return supplier?.supplier_region || '';
  };

  const cleanEmailContent = (content: string) => {
    if (!content) return '';
    
    // For received emails, try to extract just the new message
    const autoDivMatch = content.match(/<div[^>]*dir="auto"[^>]*>([^<]+)<\/div>/);
    if (autoDivMatch) {
      return autoDivMatch[1].trim();
    }
    
    // Remove only HTML tags and CSS, keep the content
    let cleaned = content
      .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '') // Remove style tags
      .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '') // Remove script tags
      .replace(/<[^>]*>/g, '') // Remove all HTML tags
      .replace(/&nbsp;/g, ' ')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&apos;/g, "'")
      .replace(/&hellip;/g, '...')
      .replace(/&mdash;/g, '—')
      .replace(/&ndash;/g, '–')
      .replace(/\s+/g, ' ') // Replace multiple spaces with single space
      .trim();
    
    // Only remove technical footers, keep the actual message content
    cleaned = cleaned
      .replace(/This email was sent from Gordian.*$/i, '') // Remove Gordian footer
      .replace(/Please do not reply to this email directly.*$/i, '') // Remove reply instruction
      .replace(/Request ID:.*$/i, '') // Remove request ID
      .replace(/unsubscribe.*$/i, '') // Remove unsubscribe
      .replace(/---- On.*$/i, '') // Remove email thread headers
      .replace(/wrote ----.*$/i, '') // Remove email thread headers
      .trim();
    
    return cleaned;
  };

  const getFilteredEmails = () => {
    let filtered = Object.values(emails);
    
    if (emailFilters.status !== 'all') {
      filtered = filtered.filter((supplierGroup: any) => 
        supplierGroup.emails.some((email: any) => email.direction === emailFilters.status)
      );
    }
    
    if (emailFilters.supplier !== 'all') {
      filtered = filtered.filter((supplierGroup: any) => 
        supplierGroup.supplier_name === emailFilters.supplier
      );
    }
    
    return filtered;
  };

  return (
    <div className="emails-tab">
      <div className="emails-container">
        {/* Left Sidebar - Email List */}
        <div className="emails-sidebar">
          <div className="emails-header">
            <h3>{t('common.emails')}</h3>
            <div className="email-filters">
              <select 
                value={emailFilters.status}
                onChange={(e) => setEmailFilters({...emailFilters, status: e.target.value})}
                className="filter-select"
              >
                <option value="all">{t('common.all_status')}</option>
                <option value="sent">{t('common.sent')}</option>
                <option value="received">{t('common.received')}</option>
              </select>
              <select 
                value={emailFilters.supplier}
                onChange={(e) => setEmailFilters({...emailFilters, supplier: e.target.value})}
                className="filter-select"
              >
                <option value="all">{t('common.all_suppliers')}</option>
                {Object.values(emails).map((supplierGroup: any) => (
                  <option key={supplierGroup.supplier_name} value={supplierGroup.supplier_name}>{supplierGroup.supplier_name}</option>
                ))}
              </select>
            </div>
          </div>

          {isLoadingEmails ? (
            <div className="loading-state">
              <p>{t('common.loading_emails')}</p>
            </div>
          ) : getFilteredEmails().length > 0 ? (
            <div className="emails-list">
              {getFilteredEmails().map((supplierGroup: any, index: number) => (
                <div 
                  key={index} 
                  className={`email-item ${selectedEmail?.supplier_name === supplierGroup.supplier_name ? 'selected' : ''}`}
                  onClick={() => handleEmailClick(supplierGroup.emails[0])}
                >
                  <div className="email-header">
                    <h4>{supplierGroup.supplier_name}</h4>
                    <span className="email-count">
                      {supplierGroup.emails.length} {supplierGroup.emails.length === 1 ? 'message' : 'messages'}
                    </span>
                  </div>
                  <div className="email-preview">
                    <p className="email-subject">{supplierGroup.emails[0]?.subject || 'No Subject'}</p>
                    <p className="email-snippet">
                      {supplierGroup.emails[0]?.content ? 
                        cleanEmailContent(supplierGroup.emails[0].content).substring(0, 100) + '...' : 
                        'No content'
                      }
                    </p>
                  </div>
                  <div className="email-meta">
                    <span className="email-date">
                      {supplierGroup.emails[0]?.sent_at || supplierGroup.emails[0]?.timestamp || supplierGroup.emails[0]?.created_at ? 
                        new Date(supplierGroup.emails[0].sent_at || supplierGroup.emails[0].timestamp || supplierGroup.emails[0].created_at).toLocaleDateString() : 
                        'No date'
                      }
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="no-results">
              <p>{t('common.no_emails_found')}</p>
            </div>
          )}
        </div>

        {/* Right Side - Email Details and Chat */}
        <div className="emails-main">
          {selectedEmail ? (
            <>
              <div className="email-info">
                <h3>{getSupplierNameFromIndex(selectedEmail.supplier_name)}</h3>
                <div className="supplier-details">
                  <p><strong>{t('common.email')}:</strong> {getSupplierEmailFromIndex(selectedEmail.supplier_name)}</p>
                  {getSupplierPhoneFromIndex(selectedEmail.supplier_name) && (
                    <p><strong>{t('common.phone')}:</strong> {getSupplierPhoneFromIndex(selectedEmail.supplier_name)}</p>
                  )}
                  {getSupplierRegionFromIndex(selectedEmail.supplier_name) && (
                    <p><strong>{t('common.region')}:</strong> {getSupplierRegionFromIndex(selectedEmail.supplier_name)}</p>
                  )}
                  <p><strong>{t('common.request')}:</strong> {request.product_name}</p>
                  <p><strong>{t('common.budget')}:</strong> {request.budget} {request.currency}</p>
                </div>
              </div>

              <div className="chat-container">
                <div className="chat-header">
                  <h4>{t('common.conversation')}</h4>
                </div>
                
                {isLoadingHistory ? (
                  <div className="loading-state">
                    <p>{t('common.loading_conversation')}</p>
                  </div>
                ) : (
                  <div className="chat-messages">
                    {emailHistory.length > 0 ? (
                      emailHistory.map((message, index) => (
                        <div 
                          key={index} 
                          className={`message ${message.direction === 'sent' ? 'sent' : 'received'}`}
                        >
                          <div className="message-content">
                            <div className="message-header">
                              <span className="message-sender">
                                {message.direction === 'sent' ? 'You' : (message.from_email ? 
                                  (message.from_email.includes('<') ? 
                                    `${message.from_email.split('<')[0].trim()} (${message.from_email.split('<')[1].replace('>', '')})` : 
                                    message.from_email
                                  ) : 'Supplier'
                                )}
                              </span>
                              {message.direction === 'sent' && message.to_email && (
                                <span className="message-recipient">
                                  → {message.to_email}
                                </span>
                              )}
                              {message.cc_recipients && Array.isArray(message.cc_recipients) && message.cc_recipients.length > 0 && (
                                <span className="message-cc" style={{
                                  fontSize: '0.8rem',
                                  color: '#666',
                                  fontStyle: 'italic',
                                  marginLeft: '8px'
                                }}>
                                  CC: {message.cc_recipients.join(', ')}
                                </span>
                              )}
                              <span className="message-time">
                                {formatTime(message.sent_at || message.created_at)}
                              </span>
                            </div>
                            <div className="message-body">
                              {message.content ? 
                                cleanEmailContent(message.content) : 
                                (message.body ? cleanEmailContent(message.body) : '')
                              }
                            </div>
                            <div className="message-actions">
                              <label style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                cursor: 'pointer',
                                fontSize: '11px',
                                color: '#374151',
                                fontWeight: '500'
                              }}>
                                <input
                                  type="checkbox"
                                  checked={selectedReplyEmail === message._id}
                                  onChange={() => {
                                    if (selectedReplyEmail === message._id) {
                                      setSelectedReplyEmail(null);
                                      setReplyToEmail(null);
                                    } else {
                                      setSelectedReplyEmail(message._id);
                                      setReplyStatusMessage('📧 Preparing reply...');
                                      handleReplyToEmail(message);
                                    }
                                  }}
                                  style={{
                                    width: '14px',
                                    height: '14px',
                                    accentColor: '#3b82f6',
                                    cursor: 'pointer'
                                  }}
                                />
                                <span>Reply</span>
                              </label>
                            </div>
                            {message.attachments && message.attachments.length > 0 && (
                              <div className="message-attachments">
                                <div className="attachments-header">📎 Attachments:</div>
                                {message.attachments.map((attachment: any, attIndex: number) => {
                                  console.log('🔍 DEBUG: RequestEmailsTab attachment:', attachment);
                                  return (
                                    <div key={attIndex} className="attachment-item">
                                      <div className="attachment-info">
                                        <span className="attachment-name">
                                          {attachment.filename || attachment.name || 'Unknown file'}
                                        </span>
                                        <span className="attachment-size">
                                          ({Math.round((attachment.size || attachment.file_size || 0) / 1024)} KB)
                                        </span>
                                      </div>
                                      <button 
                                        onClick={async () => {
                                          try {
                                            console.log('🔍 DEBUG: Download clicked for attachment:', attachment);
                                            console.log('🔍 DEBUG: attachment.attachment_id:', attachment.attachment_id);
                                            console.log('🔍 DEBUG: attachment.url:', attachment.url);
                                            console.log('🔍 DEBUG: attachment.mailgun_url:', attachment.mailgun_url);
                                            
                                            // For received emails: download from Mailgun URL first
                                            if (attachment.url) {
                                              console.log('🔍 DEBUG: Trying Mailgun URL:', `${API_ENDPOINTS.BASE_URL.replace('/api', '')}${attachment.url}`);
                                              const response = await fetch(`${API_ENDPOINTS.BASE_URL.replace('/api', '')}${attachment.url}`);
                                              if (response.ok) {
                                                const blob = await response.blob();
                                                const url = window.URL.createObjectURL(blob);
                                                const link = document.createElement('a');
                                                link.href = url;
                                                link.download = attachment.filename || attachment.name || 'attachment';
                                                document.body.appendChild(link);
                                                link.click();
                                                document.body.removeChild(link);
                                                window.URL.revokeObjectURL(url);
                                                return;
                                              } else {
                                                console.error('❌ Mailgun URL download failed:', response.status, response.statusText);
                                              }
                                            }
                                            
                                            // For sent emails: download from MongoDB (supplier documents)
                                            if (attachment.attachment_id) {
                                              console.log('🔍 DEBUG: Trying MongoDB supplier documents:', attachment.attachment_id);
                                              const response = await fetch(`${API_ENDPOINTS.BASE_URL}/supplier-products/download-document/${attachment.attachment_id}`, {
                                                headers: { 'Authorization': `Bearer ${token}` }
                                              });
                                              
                                              if (response.ok) {
                                                const blob = await response.blob();
                                                const url = window.URL.createObjectURL(blob);
                                                const link = document.createElement('a');
                                                link.href = url;
                                                link.download = attachment.filename || attachment.name || 'attachment';
                                                document.body.appendChild(link);
                                                link.click();
                                                document.body.removeChild(link);
                                                window.URL.revokeObjectURL(url);
                                                return;
                                              } else {
                                                console.error('❌ MongoDB download failed:', response.status, response.statusText);
                                              }
                                            }
                                            
                                            window.alert('Attachment ID veya URL bulunamadı.');
                                          } catch (error) {
                                            console.error('Download error:', error);
                                            window.alert('Dosya indirilemedi. Lütfen tekrar deneyin.');
                                          }
                                        }}
                                        className="attachment-download"
                                      >
                                        📥 Download
                                      </button>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="no-messages">
                        <p>{t('common.no_messages')}</p>
                      </div>
                    )}
                  </div>
                )}

                <div className="reply-section" style={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  gap: '0.5rem', 
                  padding: '0.75rem', 
                  border: '2px solid #e5e7eb', 
                  borderRadius: '8px', 
                  backgroundColor: '#ffffff', 
                  marginTop: '0.5rem',
                  position: 'relative',
                  zIndex: '9999'
                }}>
                  {/* Reply Status Message */}
                  {replyStatusMessage && (
                    <div style={{
                      padding: '8px 12px',
                      background: '#dbeafe',
                      border: '1px solid #3b82f6',
                      borderRadius: '6px',
                      fontSize: '0.85rem',
                      color: '#1e40af',
                      textAlign: 'center',
                      marginBottom: '0.5rem'
                    }}>
                      {replyStatusMessage}
                    </div>
                  )}
                  {/* Multi-recipient selection for reply */}
                  {(availableReplyEmails.length > 0 || selectedRecipients.length > 0) && (
                    <div style={{
                      marginBottom: '1rem',
                      padding: '1rem',
                      background: '#f8fafc',
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px'
                    }}>
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '0.75rem'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <h5 style={{ margin: 0, fontSize: '0.9rem', fontWeight: '600', color: '#374151' }}>
                          To:
                        </h5>
                          <button
                            type="button"
                            onClick={() => setShowToInput(!showToInput)}
                            style={{
                              padding: '4px 8px',
                              border: '1px solid #3b82f6',
                              background: '#3b82f6',
                              color: 'white',
                              borderRadius: '4px',
                              fontSize: '0.75rem',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px'
                            }}
                          >
                            <span>+</span>
                          </button>
                        </div>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button 
                            type="button" 
                            onClick={handleSelectAllReplyRecipients}
                            style={{
                              padding: '4px 8px',
                              border: '1px solid #d1d5db',
                              background: 'white',
                              color: '#374151',
                              borderRadius: '4px',
                              fontSize: '0.75rem',
                              cursor: 'pointer'
                            }}
                          >
                            All
                          </button>
                          <button 
                            type="button" 
                            onClick={handleDeselectAllReplyRecipients}
                            style={{
                              padding: '4px 8px',
                              border: '1px solid #d1d5db',
                              background: 'white',
                              color: '#374151',
                              borderRadius: '4px',
                              fontSize: '0.75rem',
                              cursor: 'pointer'
                            }}
                          >
                            None
                          </button>
                        </div>
                      </div>
                      {/* TO Input Field */}
                      {showToInput && (
                        <div style={{
                          marginBottom: '8px',
                          padding: '8px',
                          background: '#f0f9ff',
                          border: '1px solid #0ea5e9',
                          borderRadius: '6px'
                        }}>
                          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                            <input
                              type="email"
                              value={tempToEmail}
                              onChange={(e) => setTempToEmail(e.target.value)}
                              placeholder="Enter email address"
                              style={{
                                flex: 1,
                                padding: '6px 8px',
                                border: '1px solid #d1d5db',
                                borderRadius: '4px',
                                fontSize: '0.85rem',
                                fontFamily: 'Courier New, monospace'
                              }}
                            />
                            <button
                              type="button"
                              onClick={() => {
                                if (tempToEmail.trim() && !selectedRecipients.includes(tempToEmail.trim()) && !availableReplyEmails.includes(tempToEmail.trim())) {
                                  const newEmail = tempToEmail.trim();
                                  setSelectedRecipients([...selectedRecipients, newEmail]);
                                  setAvailableReplyEmails(prev => [...prev, newEmail]);
                                  setTempToEmail('');
                                  setShowToInput(false);
                                }
                              }}
                              disabled={!tempToEmail.trim()}
                              style={{
                                padding: '6px 12px',
                                border: '1px solid #10b981',
                                background: '#10b981',
                                color: 'white',
                                borderRadius: '4px',
                                fontSize: '0.8rem',
                                cursor: 'pointer',
                                opacity: tempToEmail.trim() ? 1 : 0.5
                              }}
                            >
                              Save
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setTempToEmail('');
                                setShowToInput(false);
                              }}
                              style={{
                                padding: '6px 12px',
                                border: '1px solid #ef4444',
                                background: '#ef4444',
                                color: 'white',
                                borderRadius: '4px',
                                fontSize: '0.8rem',
                                cursor: 'pointer'
                              }}
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      )}
                      
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        {availableReplyEmails.map((email, index) => (
                          <label key={index} style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            padding: '6px',
                            background: 'white',
                            border: '1px solid #e5e7eb',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '0.85rem'
                          }}>
                            <input
                              type="checkbox"
                              checked={selectedRecipients.includes(email)}
                                    onChange={() => handleReplyRecipientToggle(email)}
                              style={{ margin: 0, accentColor: '#3b82f6' }}
                            />
                            <span style={{ fontFamily: 'Courier New, monospace', color: '#374151' }}>
                              {email}
                            </span>
                          </label>
                        ))}
                      </div>
                      <div style={{
                        fontSize: '0.75rem',
                        color: '#6b7280',
                        textAlign: 'center',
                        marginTop: '8px',
                        padding: '6px',
                        background: '#f3f4f6',
                        borderRadius: '4px'
                      }}>
                        {selectedRecipients.length} of {availableReplyEmails.length} recipients selected
                      </div>
                    </div>
                  )}
                  
                  
                  {/* CC Recipients Section */}
                  {(
                    <div style={{
                      marginTop: '1rem',
                      padding: '0.75rem',
                      background: '#f8fafc',
                      border: '1px solid #e2e8f0',
                      borderRadius: '6px'
                    }}>
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '0.75rem'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <h5 style={{ margin: 0, fontSize: '0.9rem', fontWeight: '600', color: '#374151' }}>
                            CC:
                          </h5>
                          <button
                            type="button"
                            onClick={() => setShowCCInput(!showCCInput)}
                            style={{
                              padding: '4px 8px',
                              border: '1px solid #3b82f6',
                              background: '#3b82f6',
                              color: 'white',
                              borderRadius: '4px',
                              fontSize: '0.75rem',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px'
                            }}
                          >
                            <span>+</span>
                          </button>
                        </div>
                        {availableCCEmails.length > 0 && (
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <button 
                              type="button" 
                              onClick={() => setSelectedCCRecipients(availableCCEmails)}
                              style={{
                                padding: '4px 8px',
                                border: '1px solid #d1d5db',
                                background: 'white',
                                color: '#374151',
                                borderRadius: '4px',
                                fontSize: '0.75rem',
                                cursor: 'pointer'
                              }}
                            >
                              All
                            </button>
                            <button 
                              type="button" 
                              onClick={() => setSelectedCCRecipients([])}
                              style={{
                                padding: '4px 8px',
                                border: '1px solid #d1d5db',
                                background: 'white',
                                color: '#374151',
                                borderRadius: '4px',
                                fontSize: '0.75rem',
                                cursor: 'pointer'
                              }}
                            >
                              None
                            </button>
                          </div>
                        )}
                      </div>
                      {/* CC Input Field */}
                      {showCCInput && (
                        <div style={{
                          marginBottom: '8px',
                          padding: '8px',
                          background: '#f0f9ff',
                          border: '1px solid #0ea5e9',
                          borderRadius: '6px'
                        }}>
                          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                            <input
                              type="email"
                              value={tempCCEmail}
                              onChange={(e) => setTempCCEmail(e.target.value)}
                              placeholder="Enter email address"
                              style={{
                                flex: 1,
                                padding: '6px 8px',
                                border: '1px solid #d1d5db',
                                borderRadius: '4px',
                                fontSize: '0.85rem',
                                fontFamily: 'Courier New, monospace'
                              }}
                            />
                            <button
                              type="button"
                              onClick={() => {
                                if (tempCCEmail.trim() && !selectedCCRecipients.includes(tempCCEmail.trim()) && !availableCCEmails.includes(tempCCEmail.trim())) {
                                  const newEmail = tempCCEmail.trim();
                                  setSelectedCCRecipients([...selectedCCRecipients, newEmail]);
                                  setAvailableCCEmails(prev => [...prev, newEmail]);
                                  setTempCCEmail('');
                                  setShowCCInput(false);
                                }
                              }}
                              disabled={!tempCCEmail.trim()}
                              style={{
                                padding: '6px 12px',
                                border: '1px solid #10b981',
                                background: '#10b981',
                                color: 'white',
                                borderRadius: '4px',
                                fontSize: '0.8rem',
                                cursor: 'pointer',
                                opacity: tempCCEmail.trim() ? 1 : 0.5
                              }}
                            >
                              Save
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setTempCCEmail('');
                                setShowCCInput(false);
                              }}
                              style={{
                                padding: '6px 12px',
                                border: '1px solid #ef4444',
                                background: '#ef4444',
                                color: 'white',
                                borderRadius: '4px',
                                fontSize: '0.8rem',
                                cursor: 'pointer'
                              }}
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      )}
                      
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        {availableCCEmails.length > 0 ? (
                          availableCCEmails.map((email, index) => (
                          <label key={index} style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            padding: '6px',
                            background: 'white',
                            border: '1px solid #e5e7eb',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '0.85rem'
                          }}>
                            <input
                              type="checkbox"
                              checked={selectedCCRecipients.includes(email)}
                              onChange={() => {
                                if (selectedCCRecipients.includes(email)) {
                                  setSelectedCCRecipients(selectedCCRecipients.filter(e => e !== email));
                                } else {
                                  setSelectedCCRecipients([...selectedCCRecipients, email]);
                                }
                              }}
                              style={{ margin: 0, accentColor: '#3b82f6' }}
                            />
                            <span style={{ fontFamily: 'Courier New, monospace', color: '#374151' }}>
                              {email}
                            </span>
                          </label>
                        ))
                        ) : (
                          <div style={{
                            padding: '12px',
                            textAlign: 'center',
                            color: '#6b7280',
                            fontSize: '0.85rem',
                            fontStyle: 'italic'
                          }}>
                            No CC recipients yet. Use the + button above to add some.
                          </div>
                        )}
                      </div>
                      <div style={{
                        fontSize: '0.75rem',
                        color: '#6b7280',
                        textAlign: 'center',
                        marginTop: '8px',
                        padding: '6px',
                        background: '#f3f4f6',
                        borderRadius: '4px'
                      }}>
                        {selectedCCRecipients.length} of {availableCCEmails.length} CC recipients selected
                      </div>
                    </div>
                  )}
                  
                  <textarea
                    value={replyContent}
                    onChange={(e) => setReplyContent(e.target.value)}
                    placeholder={t('common.type_your_reply')}
                    rows={2}
                    style={{ 
                      padding: '0.5rem', 
                      border: '2px solid #d1d5db', 
                      borderRadius: '8px', 
                      fontSize: '0.9rem', 
                      minHeight: '60px', 
                      width: '100%', 
                      backgroundColor: '#ffffff', 
                      color: '#000000',
                      display: 'block',
                      visibility: 'visible',
                      opacity: '1',
                      position: 'relative',
                      zIndex: '10000'
                    }}
                  />
                  
                  {/* File Attachment Input */}
                  <div style={{ marginTop: '12px' }}>
                    <input
                      type="file"
                      multiple
                      onChange={handleFileChange}
                      accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif,.txt"
                      style={{
                        width: '100%',
                        padding: '8px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '0.85rem',
                        backgroundColor: '#ffffff'
                      }}
                    />
                    {replyAttachments.length > 0 && (
                      <div style={{ marginTop: '8px' }}>
                        <p style={{ 
                          fontSize: '0.8rem', 
                          color: '#374151', 
                          margin: '0 0 6px 0',
                          fontWeight: '500'
                        }}>
                          Selected files:
                        </p>
                        {replyAttachments.map((file, index) => (
                          <div key={index} style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            backgroundColor: '#f3f4f6',
                            padding: '6px 8px',
                            borderRadius: '4px',
                            marginBottom: '4px'
                          }}>
                            <span style={{ fontSize: '0.8rem', color: '#374151' }}>
                              {file.name} ({(file.size / 1024).toFixed(1)} KB)
                            </span>
                            <button
                              type="button"
                              onClick={() => removeAttachment(index)}
                              style={{
                                background: 'none',
                                border: 'none',
                                color: '#ef4444',
                                fontSize: '0.8rem',
                                cursor: 'pointer',
                                padding: '2px 4px'
                              }}
                            >
                              Remove
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <button 
                    onClick={() => {
                      console.log('🔍 DEBUG: Reply button clicked!');
                      console.log('🔍 DEBUG: replyContent:', replyContent);
                      console.log('🔍 DEBUG: selectedRecipients:', selectedRecipients);
                      console.log('🔍 DEBUG: isSendingReply:', isSendingReply);
                      handleSendReply();
                    }}
                    disabled={!replyContent.trim() || isSendingReply || selectedRecipients.length === 0}
                    style={{ 
                      alignSelf: 'flex-end', 
                      padding: '0.5rem 1rem', 
                      backgroundColor: '#3b82f6', 
                      color: '#ffffff', 
                      border: 'none', 
                      borderRadius: '8px', 
                      fontSize: '0.9rem', 
                      fontWeight: '500', 
                      cursor: 'pointer', 
                      minWidth: '100px', 
                      height: '36px',
                      display: 'block',
                      visibility: 'visible',
                      opacity: '1',
                      position: 'relative',
                      zIndex: '9999',
                      marginTop: '0.25rem',
                      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                    }}
                  >
                    {isSendingReply ? t('common.sending') : t('common.send_reply')}
                  </button>
                </div>
              </div>
            </>
          ) : (
            <div className="no-email-selected">
              <p>{t('common.select_email_to_view')}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// CSS Styles
const styles = `
  .message-actions {
    display: flex;
    justify-content: flex-end;
    margin-top: 8px;
    padding-top: 8px;
    border-top: 1px solid #e5e7eb;
  }
  
  .reply-btn {
    background-color: #3b82f6;
    color: white;
    border: none;
    border-radius: 4px;
    padding: 4px 8px;
    font-size: 12px;
    cursor: pointer;
    transition: background-color 0.2s;
  }
  
  .reply-btn:hover {
    background-color: #2563eb;
  }
  
  .reply-btn:disabled {
    background-color: #9ca3af;
    cursor: not-allowed;
  }
`;

// Inject styles
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style');
  styleSheet.textContent = styles;
  document.head.appendChild(styleSheet);
}

export default RequestEmailsTab;
